import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Link,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { toast } from 'react-hot-toast';
import { softwareApi, systemDocumentApi } from '../api/systemRegisterApi';
import { DEPLOYMENT_STATUS, DOCUMENT_TYPE, IT_DOCUMENT_TYPE, formatEnumLabel } from '../constants/enums';
import { apiErrorMessage, unwrapData, unwrapRecords } from '../utils/responseHelpers';
import SearchableSelect from 'shared/components/SearchableSelect';
import FormSection from './FormSection';

export const emptySystemDocumentRow = (variant = 'default') => ({
  key: `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  documentName: '',
  documentType: variant === 'itDocuments' ? 'IT_DOCUMENT' : '',
  relatedToSoftware: false,
  softwareId: '',
  deploymentStatus: '',
  fileName: '',
  contentType: '',
  location: '',
});

/**
 * Dialog to add / edit a system document (Compliance & Documentation).
 */
export default function SystemDocumentDialog({
  open,
  initialValues,
  onClose,
  onSave,
  saving = false,
  requireDeploymentStatus = true,
  variant = 'default',
}) {
  const isItDocuments = variant === 'itDocuments';
  const fileInputRef = useRef(null);
  const [values, setValues] = useState(emptySystemDocumentRow);
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [softwareOptions, setSoftwareOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    const incoming = initialValues || {};
    setValues({
      ...emptySystemDocumentRow(variant),
      ...incoming,
      documentType: incoming.documentType || (variant === 'itDocuments' ? 'IT_DOCUMENT' : ''),
      relatedToSoftware: Boolean(incoming.relatedToSoftware || incoming.softwareId),
      softwareId: incoming.softwareId != null && incoming.softwareId !== '' ? String(incoming.softwareId) : '',
    });
    setErrors({});
  }, [open, initialValues, variant]);

  useEffect(() => {
    if (!open || !isItDocuments) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await softwareApi.list({ page: 0, size: 200, status: 'ACTIVE' });
        const { rows } = unwrapRecords(response);
        if (cancelled) return;
        setSoftwareOptions(
          (rows || []).map((s) => ({
            id: String(s.id),
            label: s.softwareName || `Software #${s.id}`,
          }))
        );
      } catch {
        if (!cancelled) setSoftwareOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isItDocuments]);

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const relatedToSoftware = isItDocuments ? Boolean(values.relatedToSoftware) : true;
  const needsDeploymentStatus = isItDocuments ? relatedToSoftware : requireDeploymentStatus;

  const validate = () => {
    const next = {};
    if (!(values.documentName || '').trim()) next.documentName = 'Document name is required';
    if (!(values.documentType || '').trim()) {
      next.documentType = 'Document type is required';
    }
    if (relatedToSoftware && !(values.softwareId || '').trim() && isItDocuments) {
      next.softwareId = 'Select the software this document relates to';
    }
    if (needsDeploymentStatus && !(values.deploymentStatus || '').trim()) {
      next.deploymentStatus = 'Deployment status is required';
    }
    if (!(values.location || '').trim()) next.location = 'Attach a file';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      toast.error(Object.values(next)[0]);
      return false;
    }
    return true;
  };

  const handleFilePick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const response = await systemDocumentApi.upload(file);
      const uploaded = unwrapData(response);
      if (!uploaded?.location) {
        throw new Error('Upload did not return a file location');
      }
      setValues((prev) => ({
        ...prev,
        documentName: (prev.documentName || '').trim() || uploaded.documentName || '',
        fileName: uploaded.fileName || file.name,
        contentType: uploaded.contentType || file.type || '',
        location: uploaded.location,
      }));
      setErrors((prev) => {
        if (!prev.location && !prev.documentName) return prev;
        const next = { ...prev };
        delete next.location;
        if ((uploaded.documentName || file.name) && next.documentName) delete next.documentName;
        return next;
      });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  const handleOpenDocument = async () => {
    if (!values.location) {
      toast.error('No file attached to open.');
      return;
    }
    setOpening(true);
    try {
      const response = await systemDocumentApi.fetchFile(values.location);
      const contentType =
        values.contentType
        || response.headers?.['content-type']
        || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, '_blank', 'noopener,noreferrer');
      if (!opened) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.download = values.fileName || 'document';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to open document'));
    } finally {
      setOpening(false);
    }
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    void handleSave();
  };

  const handleSave = async () => {
    if (uploading || saving) return;
    if (!validate()) return;
    const linked = isItDocuments ? Boolean(values.relatedToSoftware) : true;
    await onSave?.({
      ...values,
      documentName: values.documentName.trim(),
      documentType: values.documentType || (isItDocuments ? 'IT_DOCUMENT' : ''),
      relatedToSoftware: linked,
      softwareId: linked ? (values.softwareId || '') : '',
      deploymentStatus: linked ? values.deploymentStatus : '',
      location: (values.location || '').trim(),
      fileName: (values.fileName || '').trim(),
      contentType: values.contentType || '',
    });
  };

  const dialogTitle = isItDocuments
    ? (values.id ? 'Edit IT Documents' : 'Add IT Documents')
    : (values.id ? 'Edit document' : 'Add document');

  return (
    <Dialog open={open} onClose={onClose} maxWidth={isItDocuments ? 'md' : 'sm'} fullWidth>
      <Box component="form" onSubmit={handleSubmit} noValidate>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {dialogTitle}
      </DialogTitle>
      <DialogContent dividers>
        <FormSection title={isItDocuments ? undefined : 'Document details'}>
          <Grid container spacing={2}>
            {isItDocuments ? (
              <Grid size={{ xs: 12 }}>
                <FormControl component="fieldset" error={Boolean(errors.softwareId)}>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Does this document relate to a software system?
                  </FormLabel>
                  <RadioGroup
                    row
                    value={values.relatedToSoftware ? 'software' : 'standalone'}
                    onChange={(e) => {
                      const linked = e.target.value === 'software';
                      setValues((prev) => ({
                        ...prev,
                        relatedToSoftware: linked,
                        softwareId: linked ? prev.softwareId : '',
                        deploymentStatus: linked ? prev.deploymentStatus : '',
                      }));
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.softwareId;
                        delete next.deploymentStatus;
                        return next;
                      });
                    }}
                  >
                    <FormControlLabel
                      value="software"
                      control={<Radio size="small" />}
                      label="Yes — it relates to software"
                    />
                    <FormControlLabel
                      value="standalone"
                      control={<Radio size="small" />}
                      label="No — it does not relate to a system"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: isItDocuments ? 6 : 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Document name"
                value={values.documentName ?? ''}
                onChange={(e) => setField('documentName', e.target.value)}
                error={Boolean(errors.documentName)}
                helperText={errors.documentName || ''}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: isItDocuments ? 6 : 12 }}>
              <SearchableSelect
                size="small"
                label="Document type"
                value={values.documentType ?? ''}
                onChange={(next) => setField('documentType', next)}
                error={Boolean(errors.documentType)}
                helperText={errors.documentType || ''}
                required
                options={(() => {
                  const typeOptions = isItDocuments ? IT_DOCUMENT_TYPE : DOCUMENT_TYPE;
                  const list =
                    values.documentType && !typeOptions.includes(values.documentType)
                      ? [values.documentType, ...typeOptions]
                      : typeOptions;
                  return list.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }));
                })()}
              />
            </Grid>
            {isItDocuments && relatedToSoftware ? (
              <Grid size={{ xs: 12, sm: 6 }}>
                <SearchableSelect
                  size="small"
                  label="Software"
                  value={values.softwareId ?? ''}
                  onChange={(next) => setField('softwareId', next)}
                  error={Boolean(errors.softwareId)}
                  helperText={errors.softwareId || ''}
                  required
                  options={softwareOptions.map((opt) => ({ value: opt.id, label: opt.label }))}
                />
              </Grid>
            ) : null}
            {(!isItDocuments || relatedToSoftware) ? (
            <Grid size={{ xs: 12, sm: isItDocuments ? 6 : 12 }}>
              <SearchableSelect
                size="small"
                label="Deployment status"
                value={values.deploymentStatus ?? ''}
                onChange={(next) => setField('deploymentStatus', next)}
                error={Boolean(errors.deploymentStatus)}
                helperText={errors.deploymentStatus || ''}
                required={needsDeploymentStatus}
                options={DEPLOYMENT_STATUS.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
              />
            </Grid>
            ) : null}
            <Grid size={{ xs: 12 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
                Attached file
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    handleFilePick(file);
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={14} /> : <AttachFileIcon />}
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {values.fileName ? 'Replace file' : 'Attach file'}
                </Button>
                {values.fileName ? (
                  values.location ? (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      underline="hover"
                      disabled={opening}
                      onClick={handleOpenDocument}
                      sx={{
                        cursor: opening ? 'wait' : 'pointer',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        p: 0,
                        font: 'inherit',
                      }}
                    >
                      {opening ? 'Opening…' : values.fileName}
                    </Link>
                  ) : (
                    <Typography variant="body2">{values.fileName}</Typography>
                  )
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No file attached
                  </Typography>
                )}
              </Box>
              {errors.location ? (
                <Typography variant="caption" color="error" display="block" mt={0.5}>
                  {errors.location}
                </Typography>
              ) : null}
            </Grid>
          </Grid>
        </FormSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button type="button" onClick={onClose} color="inherit" disabled={uploading || saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={uploading || saving}>
          {saving ? 'Saving…' : (isItDocuments ? 'Save IT Documents' : 'Save document')}
        </Button>
      </DialogActions>
      </Box>
    </Dialog>
  );
}
